import React, { useContext, useEffect } from 'react'
import { Navigate, useLocation, useMatch, useNavigate, useOutlet } from 'react-router-dom'

import PropTypes from 'prop-types'

import {
  CAN_BE_NAVIGATOR,
  CAN_BE_ADMIN,
  CAN_BE_SUPERUSER,
  CAN_BE_CERTIFIED,
  CAN_PROVIDE_SERVICES
} from '../../arch/constants'

import { identifyMe, loginPathWithReturnTo } from '../../auth'
import { MessagesNavigationContext } from '../../chat/contexts'
import {
  CAPABILITY_CONFIRM_EMAIL,
  CAPABILITY_UPDATE_CLIENT_INFO,
  CAPABILITY_VIEW_PRIMARY_UI
} from '../../arch/capabilityConstants'
import { FEATURE_IEP_REVIEW } from '../../arch/iepConstants'
import { FEATURE_STATUS_OFF, FEATURE_STATUS_ON, FEATURES_STATUS_FAILED, FEATURES_STATUS_LOADING } from '../../constants'
import { useIepReviewRedirect } from '../../onboarding/iep-review/hooks'

import { ONBOARDING_LANDING, ZIP_CODE_REQUIRED_PATH } from '../../onboarding/constants'
import { LoginContext, NotificationsContext } from '../contexts'
import { useAppUser, useCurrentSite, useFeatureStatus, useParentViewCapability } from '../hooks'

import ErrorBoundary from './ErrorBoundary'
import FeatureUnavailable from './FeatureUnavailable'
import LoadingFallback from './LoadingFallback'
import NotFound from './NotFound'
import ThemedContainer from './ThemedContainer'

/**
 * The MainLayout component captures a range of common behavior that all main-level routes
 * (i.e., components that occupy the main area of the web app) can use. These include but are
 * not limited to:
 *
 * - An error boundary
 * - Recording/logging of analytics
 * - Eliminating a logged-in user if requested
 *
 * @param {object} props
 */
const MainLayout = props => {
  const { children, clearUser } = props

  const outlet = useOutlet()
  const { setAppUser } = useContext(LoginContext)

  useEffect(() => {
    identifyMe()
  }, [])

  useEffect(() => {
    // Check if the caller wants to ensure that there isn’t a user.
    if (clearUser) {
      setAppUser(null)
    }
  }, [clearUser, setAppUser])

  return <ErrorBoundary>{children || outlet}</ErrorBoundary>
}

/**
 * Returns the props of a Navigate to `/zipcode-required` if the logged-in parent still owes us a zip code (e.g., they
 * left onboarding without entering one, or are an old account that never had one), or `null` otherwise. Every gate
 * asks before showing its page, so such a parent is prompted for the zip code before reaching anything but onboarding,
 * the zip code page itself, and the open routes.
 * The original destination is passed along so the user lands back there after entering the zip code, and the redirect
 * replaces the page it leaves so that Back doesn’t bounce the parent right back to the zip code page.
 */
const useZipcodeRequiredRedirect = () => {
  const appUser = useAppUser()
  const location = useLocation()
  const { isParentCapable, isUserCapable } = useParentViewCapability()

  const isOnboardingPath = useMatch(`/${ONBOARDING_LANDING}`)
  const isZipcodeRequiredPath = useMatch(`/${ZIP_CODE_REQUIRED_PATH}`)

  // An unfinished IEP review account may stay on the pages that allow it without a zip code (see useIepReviewRedirect).
  const { zipCodeSkipped } = useIepReviewRedirect()

  const isZipcodeRequired =
    appUser?.isParent() &&
    isUserCapable(CAPABILITY_VIEW_PRIMARY_UI) &&
    isParentCapable(CAPABILITY_UPDATE_CLIENT_INFO) &&
    !isZipcodeRequiredPath &&
    !isOnboardingPath &&
    !zipCodeSkipped

  return isZipcodeRequired
    ? {
        to: `/${ZIP_CODE_REQUIRED_PATH}`,
        state: { redirectDestinationPath: location.pathname + location.search + location.hash },
        replace: true
      }
    : null
}

/**
 * UserGate wraps its outlet around a check for a logged-in user, independent of role.
 *
 * @param {object} props
 */
const UserGate = props => {
  const appUser = useAppUser()
  const location = useLocation()
  const outlet = useOutlet()

  const zipcodeRequiredRedirect = useZipcodeRequiredRedirect()

  const { children } = props

  // If no user, we go to login.
  // returnTo carries a destination through hard refreshes and Google SSO
  if (!appUser) {
    return <Navigate to={loginPathWithReturnTo(location)} state={{ from: location }} />
  }

  if (zipcodeRequiredRedirect) {
    return <Navigate {...zipcodeRequiredRedirect} />
  }

  return children || outlet
}

/**
 * RoleGate wraps its outlet around a check for roles.
 *
 * @param {object} props
 */
const RoleGate = props => {
  const appUser = useAppUser()
  const location = useLocation()

  const outlet = useOutlet()

  const isOnboardingPath = useMatch(`/${ONBOARDING_LANDING}`)
  const zipcodeRequiredRedirect = useZipcodeRequiredRedirect()

  const { acceptableRoles, children } = props

  const { isParentCapable, isUserCapable } = useParentViewCapability()
  const iepReview = useIepReviewRedirect()

  const iepReviewFlag = useFeatureStatus(FEATURE_IEP_REVIEW)

  // If no user, we go to login.
  // // returnTo carries a destination through hard refreshes and Google SSO
  if (!appUser) {
    return <Navigate to={loginPathWithReturnTo(location)} state={{ from: location }} />
  }

  // Guard for unconfirmed email, if the logged-in user is a provider.
  if (appUser.isProvider() && isParentCapable(CAPABILITY_CONFIRM_EMAIL)) {
    return <Navigate to="/confirm-email" />
  }

  // Quick guard for unconfirmed email, if the logged-in user is a parent.
  if (appUser.isParent()) {
    if (!isUserCapable(CAPABILITY_VIEW_PRIMARY_UI) && !isOnboardingPath) {
      if (isUserCapable(CAPABILITY_CONFIRM_EMAIL)) {
        return <Navigate to="/confirm-email" />
      } else {
        // This should actually never happen, and is otherwise indicative of a deeper, more serious problem.
        // As such, we send the user to a bogus route which will look like a 404.
        return <Navigate to="/no-primary-ui-no-email" />
      }
    }

    // The redirect replaces the page it leaves, so that Back doesn’t bounce the parent right back into it. Flags
    // that couldn’t be fetched are fetched again by the app; until then, the account gets the ordinary routes.
    if (iepReview.target) {
      if (iepReviewFlag === FEATURES_STATUS_LOADING) {
        return <LoadingFallback />
      }

      if (iepReviewFlag === FEATURE_STATUS_ON) {
        return <Navigate to={iepReview.target} state={iepReview.targetState} replace />
      }
    }

    if (zipcodeRequiredRedirect) {
      return <Navigate {...zipcodeRequiredRedirect} />
    }
  }

  // If the user has sufficient roles, we render the outlet; otherwise we say 404
  // so as not to give away that a path might be valid for other roles.
  return appUser.role(acceptableRoles) || appUser.isProvider() ? children || outlet : <NotFound />
}

/**
 * ParentGate is a little more specialized than RoleGate because here we are factoring in whether
 * the current site specifies `withChildrenOnly`—this eliminates care team users.
 */
const ParentGate = props => {
  const appUser = useAppUser()
  const currentSite = useCurrentSite()
  const { withChildrenOnly } = currentSite

  const navigate = useNavigate()
  const { chatMessageRequest } = useContext(NotificationsContext)
  const { navigatorMessagesPath } = useContext(MessagesNavigationContext)

  useEffect(() => {
    if (chatMessageRequest) {
      navigate(navigatorMessagesPath())
    }
  }, [chatMessageRequest, navigate, navigatorMessagesPath])

  // Pre-emptive check for `withChildrenOnly`.
  const hasChildren = appUser?.household?.clients?.length > 0
  return withChildrenOnly && appUser && !hasChildren ? <NotFound /> : <RoleGate {...props} />
}

/**
 * Specialized gate meant for knowledge base articles.
 *
 * @param {object} props
 */
const ResourceGate = props => {
  const appUser = useAppUser()
  const outlet = useOutlet()

  const { children } = props

  const { isParentCapable } = useParentViewCapability()
  const zipcodeRequiredRedirect = useZipcodeRequiredRedirect()

  const { location } = window
  const { pathname, search } = location

  if (!appUser) {
    // This is outside the app, so we assign directly to window.location.
    // Extract everything after `/resources/`
    const pathAfterResources = pathname.split('/resources/')[1]

    location.href = `/resources${pathAfterResources ? `/${pathAfterResources}` : ''}${search}`
    return <LoadingFallback message="Loading Undivided resources…" />
  }

  // Guard for unconfirmed email, if the logged-in user is a provider.
  if (appUser.isProvider() && isParentCapable(CAPABILITY_CONFIRM_EMAIL)) {
    return <Navigate to="/confirm-email" />
  }

  if (zipcodeRequiredRedirect) {
    return <Navigate {...zipcodeRequiredRedirect} />
  }

  return children || outlet
}

/**
 * ThemeLayout wraps its outlet around a particular theme.
 */
const ThemeLayout = props => {
  const { theme, children } = props
  const outlet = useOutlet()

  return <ThemedContainer theme={theme}>{children || outlet}</ThemedContainer>
}

const FeatureGate = props => {
  const { feature, children } = props

  const status = useFeatureStatus(feature)

  if (status === FEATURES_STATUS_LOADING) {
    return <LoadingFallback />
  }

  if (status === FEATURES_STATUS_FAILED) {
    return <FeatureUnavailable />
  }

  if (status === FEATURE_STATUS_OFF) {
    return <NotFound />
  }

  return children
}

FeatureGate.propTypes = {
  feature: PropTypes.string.isRequired, // Flag name from the `features` list at the API root.
  children: PropTypes.node // Rendered only while the flag is on.
}

const AdminGate = props => <RoleGate {...props} acceptableRoles={CAN_BE_ADMIN} />
const NavigatorGate = props => <RoleGate {...props} acceptableRoles={CAN_BE_NAVIGATOR} />
const CertifiedMemberGate = props => <RoleGate {...props} acceptableRoles={CAN_BE_CERTIFIED} />
const ServicesGate = props => <RoleGate {...props} acceptableRoles={CAN_PROVIDE_SERVICES} />
const SuperuserGate = props => <RoleGate {...props} acceptableRoles={CAN_BE_SUPERUSER} />

const InternalUserGate = props => (
  <RoleGate {...props} acceptableRoles={[...CAN_PROVIDE_SERVICES, ...CAN_BE_ADMIN, ...CAN_BE_SUPERUSER]} />
)

export {
  ThemeLayout,
  MainLayout,
  UserGate,
  ParentGate,
  ResourceGate,
  AdminGate,
  NavigatorGate,
  CertifiedMemberGate,
  ServicesGate,
  SuperuserGate,
  InternalUserGate,
  FeatureGate
}
